import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, TableSortLabel } from '@mui/material';
import { calculateActivityScores } from '../utils/activityUtils';

const PanelSummary = () => {
  const users = useSelector((state) => state.users.users);
  const achievements = useSelector((state) => state.achievements.achievements);
  const activities = useSelector((state) => state.activities.activities);
  const scoring = useSelector((state) => state.scoring.scoring);
  const badges = useSelector((state) => state.scoring.badges);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  const calculateScores = (userId) => {
    const userAchievements = achievements.filter(ach => ach.coreId === userId);
    const categoryScores = {};
    const userBadges = [];

    userAchievements.forEach((ach) => {
      const stage = scoring.find(stage => stage.id === ach.actionId);
      if (stage) {
        const category = stage.category;
        if (!categoryScores[category]) {
          categoryScores[category] = 0;
        }
        categoryScores[category] += stage.score;
        if (categoryScores[category] > stage.totalPoints) {
          categoryScores[category] = stage.totalPoints;
        }
      }
      const badge = badges.find(badge => badge.id === ach.badgeId);
      if (badge) {
        userBadges.push(badge.badge);
      }
    });

    const activityScores = calculateActivityScores(activities.filter(act => act.coreId === userId), scoring);
    
    return { categoryScores, userBadges, activityScores };
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedUsers = users.slice().sort((a, b) => {
    if (orderBy === 'name') {
      return order === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    } else if (orderBy === 'coreId') {
      return order === 'asc' ? a.coreId.localeCompare(b.coreId) : b.coreId.localeCompare(a.coreId);
    } else if (orderBy === 'scores') {
      const aScores = Object.values(calculateScores(a.coreId).categoryScores).reduce((acc, score) => acc + score, 0);
      const bScores = Object.values(calculateScores(b.coreId).categoryScores).reduce((acc, score) => acc + score, 0);
      return order === 'asc' ? aScores - bScores : bScores - aScores;
    } else if (orderBy === 'badges') {
      const aBadges = calculateScores(a.coreId).userBadges.length;
      const bBadges = calculateScores(b.coreId).userBadges.length;
      return order === 'asc' ? aBadges - bBadges : bBadges - aBadges;
    }
    return 0;
  });

  return (
    <div>
      <Typography variant="h4">Summary</Typography>
      {users && achievements && scoring && badges && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={(event) => handleRequestSort(event, 'name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'coreId'}
                    direction={orderBy === 'coreId' ? order : 'asc'}
                    onClick={(event) => handleRequestSort(event, 'coreId')}
                  >
                    Core ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'scores'}
                    direction={orderBy === 'scores' ? order : 'asc'}
                    onClick={(event) => handleRequestSort(event, 'scores')}
                  >
                    Scores
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'badges'}
                    direction={orderBy === 'badges' ? order : 'asc'}
                    onClick={(event) => handleRequestSort(event, 'badges')}
                  >
                    Badges
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => {
                const { categoryScores, userBadges, activityScores } = calculateScores(user.coreId);
                return (
                  <TableRow key={user.coreId}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.coreId?.toUpperCase()}</TableCell>
                    <TableCell>
                      {Object.entries(categoryScores).map(([category, score]) => (
                        <div key={category}>{category}: {score}</div>
                      ))}
                      {Object.entries(activityScores).map(([activityId, score]) => (
                        <div key={activityId}>{activityId}: {score}</div>
                      ))}
                    </TableCell>
                    <TableCell>
                      {userBadges.join(', ')}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default PanelSummary;

